/**
 * React hook for alert management
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Alert, AlertFilters, AlertMonitoringStatus } from '../../../types/alert';
import { processAlert } from '../utils/alertProcessors';

export interface UseAlertsResult {
  alerts: Alert[];
  filteredAlerts: Alert[];
  loading: boolean;
  error: string | null;
  monitoringStatus: AlertMonitoringStatus | null;
  refreshAlerts: () => Promise<void>;
  filterAlerts: (filters: AlertFilters) => void;
  getAlertStats: () => Promise<{
    critical: number;
    warning: number;
    info: number;
    low: number;
    total: number;
  }>;
  markAlertsViewed: (alertIds: number[]) => Promise<void>;
}

export const useAlerts = (autoRefresh: boolean = true, refreshInterval: number = 10000): UseAlertsResult => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [filteredAlerts, setFilteredAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [monitoringStatus, setMonitoringStatus] = useState<AlertMonitoringStatus | null>(null);
  const [filters, setFilters] = useState<AlertFilters>({});
  const isFetchingRef = useRef(false);

  const refreshAlerts = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // Fetch alerts through Django backend proxy (resolves CORS issues)
      const response = await fetch(`/api/alerts/get/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          limit: 50,
          recent_minutes: 60,
          offset: 0,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch alerts from backend');
      }

      const data = await response.json();
      const alertsData = data.alerts || [];

      // If backend returned success: false, there was an API issue
      if (!data.success && data.error) {
        console.warn('Backend API error:', data.error);
        // Still set alerts to empty array rather than throwing error
        setAlerts([]);
      } else {
        // Currently configured to only allow certain alert types, need to expand and filter based on user role
        const alllowedAlerts = new Set([
          'ROBOT_ARRIVAL_FAILURE',
          'alert_robot_video_start',
          'alert_robot_video_end',
          'alert_help_info_activated',
        ]);
        // Process and enrich alerts with notification text, severity, and category
        const processedAlerts: Alert[] = alertsData
          .filter((alertData: any) => alllowedAlerts.has(alertData.task))
          .map((alertData: any) => {
            return processAlert(alertData);
          });
        setAlerts(processedAlerts);
      }

      // Set monitoring status based on backend response
      setMonitoringStatus({
        monitor_running: data.success,
        nhb_api_status: data.success && alertsData.length >= 0,
        last_check_time: new Date().toISOString(),
        email_recipients_count: 0, // We'll implement localStorage-based email management
        fetch_interval_minutes: 5,
        fetch_limit: 50,
        processed_alerts_count: alertsData.length,
        email_config_status: false,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch alerts');

      // Set error status
      setMonitoringStatus({
        monitor_running: false,
        nhb_api_status: false,
        last_check_time: new Date().toISOString(),
        email_recipients_count: 0,
        fetch_interval_minutes: 5,
        fetch_limit: 50,
        processed_alerts_count: 0,
        email_config_status: false,
      });
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  const filterAlerts = useCallback(
    (newFilters: AlertFilters) => {
      setFilters(newFilters);

      let filtered = alerts.slice();

      // Filter by severity (use processed severity field, fallback to log.level)
      if (newFilters.severity && Array.isArray(newFilters.severity)) {
        filtered = filtered.filter(alert => {
          const severity = alert.severity || alert.log?.level;
          return severity && newFilters.severity!.includes(severity as any);
        });
      }

      // Filter by time range
      if (newFilters.timeRange) {
        const now = new Date();
        let hours = 1;
        switch (newFilters.timeRange) {
          case 'hour':
            hours = 1;
            break;
          case '6hours':
            hours = 6;
            break;
          case '24hours':
            hours = 24;
            break;
          case '48hours':
            hours = 48;
            break;
        }
        const limitTime = new Date(now.getTime() - hours * 60 * 60 * 1000);

        filtered = filtered.filter(alert => {
          const alertTime = new Date(alert.ts);
          return alertTime >= limitTime;
        });
      }

      // Search filter
      if (newFilters.searchTerm) {
        const searchTerm = newFilters.searchTerm.toLowerCase();
        filtered = filtered.filter(
          alert =>
            alert.content.toLowerCase().includes(searchTerm) ||
            alert.robot.toLowerCase().includes(searchTerm) ||
            alert.museum.toLowerCase().includes(searchTerm),
        );
      }

      setFilteredAlerts(filtered);
    },
    [alerts],
  );

  const getAlertStats = useCallback(async () => {
    const stats = {
      total: alerts.length,
      critical: alerts.filter(alert => {
        const severity = (alert.severity || alert.log?.level)?.toLowerCase();
        return severity === 'critical';
      }).length,
      warning: alerts.filter(alert => {
        const severity = (alert.severity || alert.log?.level)?.toLowerCase();
        return severity === 'warning' || severity === 'high';
      }).length,
      info: alerts.filter(alert => {
        const severity = (alert.severity || alert.log?.level)?.toLowerCase();
        return severity === 'info';
      }).length,
      low: alerts.filter(alert => {
        const severity = (alert.severity || alert.log?.level)?.toLowerCase();
        return severity === 'low';
      }).length,
    };
    return stats;
  }, [alerts]);

  const markAlertsViewed = useCallback(async (alertIds: number[]) => {
    if (!alertIds.length) return;

    try {
      const response = await fetch('/api/alerts/viewed/', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ alert_ids: alertIds }),
      });

      if (!response.ok) {
        throw new Error('Failed to mark alerts as viewed');
      }

      const data = await response.json();
      console.log(`Marked ${data.viewed_count} alerts as viewed`);

      // Store in localStorage as backup
      const viewedAlerts = JSON.parse(localStorage.getItem('viewedAlerts') || '[]');
      const updatedViewed = [...viewedAlerts, ...alertIds];
      localStorage.setItem('viewedAlerts', JSON.stringify(Array.from(new Set(updatedViewed)))); // Remove duplicates
    } catch (error) {
      console.error('Failed to mark alerts as viewed:', error);
    }
  }, []);

  // Apply filters whenever alerts or filters change
  useEffect(() => {
    filterAlerts(filters);
  }, [alerts, filters, filterAlerts]);

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefresh) {
      // Initial fetch
      refreshAlerts();

      // Set up interval
      const interval = setInterval(refreshAlerts, refreshInterval);

      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, refreshAlerts]);

  return {
    alerts,
    filteredAlerts,
    loading,
    error,
    monitoringStatus,
    refreshAlerts,
    filterAlerts,
    getAlertStats,
    markAlertsViewed,
  };
};
